/**
 * Läuft die Anwendung vom Home-Bildschirm oder im Browser?
 *
 * **Gebraucht wird das für zwei unangenehme Dinge und eine Höhe:**
 * `window.print()` ist in einer vom Home-Bildschirm gestarteten Web-App auf
 * iOS nicht verlässlich — die Druckvorschau bleibt dort hängen. Im Browser
 * funktioniert derselbe Aufruf. Gemeldet vom iPhone am Buch (#14): „der Button
 * zum Drucken funktioniert auch nicht." Die Tastaturmechanik (`lib/tastatur.ts`)
 * rechnet in der App mit der Mindesthöhe des Bildschirms; und seit 1.53.1
 * hängt daran, ob unter iOS 27 ein Rand für das Glasband oben freigehalten
 * wird (`lib/geraet.ts`).
 *
 * **`navigator.standalone` allein reicht nicht**, und das kam aus dem Betrieb:
 * mit der Mindesthöhe aus `d4b4176` stand auf dem iPhone weiterhin
 * `Gesetzte Höhe: 812px` bei `Bildschirm: 402 × 874`, auf dem iPad `1000` bei
 * `1032` -- sie griff auf keinem der beiden Geräte. Der Grund ist Apples
 * Vorläufer selbst: `navigator.standalone` ist nie standardisiert worden und
 * **auf iPadOS gar nicht vorhanden**. Eine Prüfung darauf schließt also genau
 * das Gerät aus, auf dem der Fehler zuerst gemeldet wurde.
 *
 * Der Standard dafür ist die Medienabfrage `display-mode`. Sie steht zuerst;
 * `navigator.standalone` bleibt als Rückfall für ältere Safari-Versionen
 * daneben. Abgefragt werden alle drei Betriebsarten, die kein Browser-Tab
 * sind -- die Anzeigeart steht im `manifest.webmanifest` und kann sich ändern,
 * ohne dass jemand hier nachsieht.
 *
 * **Bis 1.53.1 stand diese Frage zweimal** -- hier mit einer Betriebsart und
 * in `lib/tastatur.ts` mit dreien. Jetzt einmal. Die Diagnosekarte
 * (`SettingsPage`) fragt bewusst anders: sie will In-App-Browser von der App
 * unterscheiden und sagt dort, warum.
 *
 * **Kein Rückschluss auf das Betriebssystem.** Gefragt wird, wie die Anwendung
 * gestartet wurde, nicht auf welchem Gerät — eine Kennung auszuwerten wäre
 * dieselbe Rateaufgabe in schlechter.
 */
export function alsApp(): boolean {
  if (typeof window === 'undefined') return false
  const frage = window.matchMedia?.bind(window)
  if (frage !== undefined) {
    for (const art of ['standalone', 'fullscreen', 'minimal-ui']) {
      if (frage(`(display-mode: ${art})`).matches) return true
    }
  }
  // Apples Vorläufer: auf dem iPhone seit jeher, auf dem iPad nie.
  return (window.navigator as { standalone?: boolean }).standalone === true
}

/**
 * Läuft das hier wirklich als Web-App vom Startbildschirm?
 *
 * **Strenger als `alsApp()`, und der Unterschied ist der Zweck.** Oben geht
 * es darum, wie die Anwendung gestartet wurde; hier darum, ob jemand sie
 * noch installieren soll. Ein In-App-Browser meldet `display-mode:
 * standalone`, weil aus Sicht der Seite keine Leisten da sind -- wer danach
 * ginge, nähme den Knopf ausgerechnet dem weg, der ihn braucht. Auf iOS
 * zählt deshalb `navigator.standalone` und sonst nichts.
 *
 * Stand bis 1.56.0 als `isStandalone()` in `SettingsPage.tsx`; seit dem
 * Knopf „Als App hinzufügen" (#101) fragen zwei Stellen danach, also steht
 * sie hier.
 */
export function vomStartbildschirm(): boolean {
  if (typeof window === 'undefined') return false
  const apple = (window.navigator as { standalone?: boolean }).standalone
  if (apple !== undefined) return apple
  return window.matchMedia('(display-mode: standalone)').matches
}
